import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  headerTheme?: "navy" | "light";
  hideHeader?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "lg",
  closeOnBackdrop = true,
  closeOnEsc = true,
  headerTheme = "navy",
  hideHeader = false,
}) => {
  // Lock body scroll when modal is active
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const modalNode = (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto animate-in fade-in duration-150"
      onClick={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} max-h-[92vh] sm:max-h-[88vh] flex flex-col overflow-hidden border border-gray-100 my-auto animate-in zoom-in-95 duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        {!hideHeader && (
          <div
            className={`px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between shrink-0 gap-3 border-b ${
              headerTheme === "navy"
                ? "bg-[#041B44] text-white border-transparent"
                : "bg-white text-gray-900 border-gray-100"
            }`}
          >
            <div className="min-w-0 flex-1">
              {title && (
                <h3 className="font-bold text-sm sm:text-base tracking-tight truncate">
                  {title}
                </h3>
              )}
              {subtitle && (
                <div
                  className={`text-[11px] sm:text-xs mt-0.5 truncate ${
                    headerTheme === "navy" ? "text-gray-300 font-mono" : "text-gray-500"
                  }`}
                >
                  {subtitle}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-base cursor-pointer transition-colors shrink-0 ${
                headerTheme === "navy"
                  ? "text-gray-300 hover:text-white hover:bg-white/10"
                  : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              }`}
              aria-label="Close dialog"
            >
              ✕
            </button>
          </div>
        )}

        {/* Modal Body - Scrollable Container */}
        <div className="p-3.5 sm:p-6 space-y-3 sm:space-y-4 flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          {children}
        </div>

        {/* Modal Footer */}
        {footer && (
          <div className="p-3 sm:p-4 px-4 sm:px-6 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalNode, document.body);
};

export default Modal;
